/** Cart drawer on a native <dialog>, listening to cart:update (shared by CartAddEvent and CartUpdateEvent); on refresh only the inner content is replaced, never the <dialog> itself, or the modal would close and reopen. */

import { Component } from "@theme/component";
import { CartAddEvent, ThemeEvents } from "@theme/events";

/**
 * @typedef {Object} CartDrawerRefs
 * @property {HTMLDialogElement} dialog
 * @extends {Component<CartDrawerRefs>}
 */
class CartDrawerComponent extends Component {
  requiredRefs = ["dialog"];

  #scrollLocked = false;

  /** @type {AbortController | null} */
  #refreshController = null;

  /** Bumped on every render request, so a slow fetch never overwrites newer HTML. */
  #renderVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    this.refs.dialog.addEventListener("click", this.#handleBackdropClick);
    this.refs.dialog.addEventListener("cancel", this.#unlockScroll);
    this.refs.dialog.addEventListener("close", this.#unlockScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    this.#refreshController?.abort();
    this.#unlockScroll();
  }

  #lockScroll = () => {
    if (this.#scrollLocked) return;
    this.#scrollLocked = true;
    // Lock on <html> only: overflow:hidden on <body> as well turns <body> into a scroll container, which un-pins every position:sticky it holds (the full-height dyptique image jumps back into the flow, pausing its video).
    document.documentElement.style.overflow = "hidden";
    document.dispatchEvent(new CustomEvent("drawer:open"));
  };

  #unlockScroll = () => {
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    document.documentElement.style.overflow = "";
    document.dispatchEvent(new CustomEvent("drawer:close"));
  };

  /** Id de la <section class="shopify-section"> parente (sans le préfixe). */
  get sectionId() {
    const el = this.closest(".shopify-section");
    if (!el) throw new Error("<cart-drawer-component> must be inside a Shopify section");
    return el.id.replace(/^shopify-section-/, "");
  }

  open() {
    if (!this.refs.dialog.open) {
      this.refs.dialog.showModal();
      this.refs.dialog.focus();
    }
    this.#lockScroll();
    this.querySelectorAll("slide-show").forEach((s) => s.restart?.());
  }

  close() {
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#unlockScroll();
  }

  /** Re-fetch la section et remplace le contenu interne du <dialog> (sans fermer le drawer). */
  async refresh() {
    const version = ++this.#renderVersion;
    this.#refreshController?.abort();
    const controller = new AbortController();
    this.#refreshController = controller;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", this.sectionId);

      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      if (version === this.#renderVersion) this.#swapInner(html);
    } catch (error) {
      if (error.name !== "AbortError") console.error("[cart-drawer] refresh failed:", error);
    }
  }

  /** Renders section HTML returned by the Section Rendering API (bundled in a cart/add or cart/change response). */
  render(html) {
    ++this.#renderVersion;
    this.#refreshController?.abort();
    this.#swapInner(html);
  }

  #swapInner(html) {
    const fragment = new DOMParser().parseFromString(html, "text/html");
    const newInner = fragment.querySelector("[data-cart-drawer-inner]");
    const currentInner = this.querySelector("[data-cart-drawer-inner]");

    if (!newInner || !currentInner) {
      console.error("[cart-drawer] inner content not found");
      return;
    }

    currentInner.replaceWith(newInner);
  }

  /** Any cart change: renders the bundled section HTML when present (else re-fetches); only an add opens the drawer. */
  #handleCartChange = async (event) => {
    const html = event.detail?.data?.sections?.[this.sectionId];
    if (html) {
      this.render(html);
    } else {
      await this.refresh();
    }

    if (event instanceof CartAddEvent) this.open();
  };

  /** <dialog> natif ne se ferme pas au clic backdrop : on détecte le clic sur le <dialog> lui-même. */
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
